import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-typing-indicator>` says about itself.
 *
 * A one-line piece with one input, and two things about it worth reading before placing it.
 *
 * It has no lifetime of its own. Nothing here decides when somebody is typing or when they stopped
 * — a surface adds the element and removes it, and an indicator that is never removed says somebody
 * is typing forever. That is deliberate: the only source that knows is whatever is delivering the
 * conversation, and a component that guessed with a timer would be wrong on a slow connection in
 * exactly the case it was added for.
 *
 * Its sentence is a live region, so it is ANNOUNCED. That is the reason it is one line of text and
 * not three animated dots alone: dots say nothing to a screen reader, and a reader who cannot see
 * them would otherwise have no way to know a reply was on its way.
 */
const docs: ComponentDocs = {
  aliases: ['is typing', 'typing dots', 'composing indicator', 'presence typing', 'writing indicator'],

  answers: [
    'show that somebody is writing a reply',
    'tell the reader a message is coming',
    'show the assistant is working on an answer',
  ],

  // A conversation can stand on a home page, an app page or a record page — the indicator goes
  // wherever the stream it belongs to goes, and nowhere on its own.
  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'who',
      attribute: 'who',
      type: 'text',
      description:
        'The name of the person composing. Unset, the line says that someone is typing without ' +
        'claiming who — which is what a surface that knows a reply is coming but not from whom should ' +
        'say.',
    },
  ],

  // It reports nothing: it is told that somebody is typing and says so.
  events: [],
  state: [],
  slots: [],

  tokens: [
    { token: '--caos-color-text-muted', controls: 'The sentence and the dots — quiet, because it is not the message.' },
    { token: '--caos-font', controls: 'The typeface the sentence is set in.' },
    { token: '--caos-text-row', controls: 'The size of the sentence, matched to a dense row rather than to body text.' },
    { token: '--caos-space-2', controls: 'The gap between the sentence and the dots.' },
    { token: '--caos-space-1', controls: 'The space above and below the line.' },
    { token: '--caos-space-3', controls: 'The inset from the edge of the stream, so it lines up with the messages.' },
    { token: '--caos-radius-pill', controls: 'The roundness of each dot.' },
  ],

  parts: [
    { part: 'indicator', is: 'the whole line' },
    { part: 'label', is: 'the sentence' },
    { part: 'dots', is: 'the three animated dots' },
  ],

  methods: [],

  placing:
    'At the foot of a message stream, below the last message and above the composer, where the next ' +
    'message will appear. `<caos-message-stream>` has a slot in exactly that position; placed ' +
    'anywhere else it is a line of text that happens to animate.',

  whenToUse:
    'While a reply is genuinely being composed and its arrival is expected within a few seconds — a ' +
    'person typing, or an assistant generating. It is a promise that something is coming, so showing ' +
    'it when nothing is on its way is worse than showing nothing.',

  insteadUse: [
    {
      name: 'SkeletonBlock',
      when:
        'Content is loading rather than being written. A skeleton says "this is arriving"; this says ' +
        '"somebody is deciding what to say", which is a different wait with a different length.',
    },
    {
      name: 'ProgressBar',
      when: 'The wait has a measurable end. Nobody knows how long a sentence takes to write.',
    },
    {
      name: 'StatusDot',
      when:
        'The subject is whether somebody is present or reachable at all, which is a steady condition ' +
        'rather than a thing they are doing this second.',
    },
  ],

  accessibility:
    'The line is a `role="status"` live region, so it is announced without taking the reader away from ' +
    'what they were reading — `status` and not `alert`, because somebody starting to type is not ' +
    'urgent enough to interrupt a sentence in progress. The dots are `aria-hidden`: they carry nothing ' +
    'the sentence does not. Under `prefers-reduced-motion: reduce` they stop moving entirely, which ' +
    'matters more here than on most animations because this one has no end of its own — it runs until ' +
    'somebody else finishes typing. Left to the author: removing the element when the message arrives. ' +
    'Nothing here can know that, and a live region that keeps saying someone is typing is a false ' +
    'statement that a screen reader keeps repeating.',

  examples: [
    {
      key: 'named',
      label: 'While a colleague is replying',
      summary: 'Named, at the foot of the stream.',
      placement: { component: 'caos-typing-indicator', inputs: { who: 'Ada Lovelace' } },
    },
    {
      key: 'anonymous',
      label: 'When the source is not known',
      summary:
        'No name: the surface knows a reply is coming but not from whom, and the line says exactly ' +
        'that rather than guessing.',
      placement: { component: 'caos-typing-indicator' },
    },
  ],
};

export default docs;
